import { parsePrice } from "./format";

export const rowKey = (row) => `${row.bitrix_id}__${row.source}__${row.manufacturer}`;

// Группирует строки каталога по номенклатуре, фильтрует по региону/поиску
// и считает минимальную цену по выбранному основанию (нал/безнал).
//
// search здесь должен быть уже дебаунснутым (см. useCatalogSearch:
// debouncedSearch), а не живым текстом из инпута — иначе на каждую нажатую
// клавишу список либо мигает локальным .includes()-фильтром, либо (если
// smartScoreByName ещё не инвалидирован) фильтруется по карте от другого,
// более старого запроса.
//
// smartScoreByName (опционально) — Map<имя_номенклатуры, score> от умного поиска
// (KAL-59, search-service, см. useCatalogSearch). null — умный поиск ещё не
// использовался или недоступен, тогда фильтрация/сортировка откатываются на
// обычный локальный .includes()-фильтр и алфавитный порядок. Индексируется по
// name, а не по id: warehouse_stock_unique —
// VIEW без стабильного id, у search-service там id — просто row_number() и не
// соответствует ничему на фронте, см. комментарий у /product-catalog/search-names.
//
// Поиск идёт по номенклатуре, категории/разделам Битрикса (root_section,
// section, category) и производителю. По производителю раньше давал ложные
// совпадения вида "бетон" -> "Опора освещения" из-за поставщика "ПН-БЕТОН
// ООО", но это осознанный компромисс по запросу — поиск по названию
// производства нужен как отдельный сценарий.
export const groupCatalogItems = (catalogData, { region, search, priceBasis, smartScoreByName = null }) => {
    const searchNormalized = search.trim().toLowerCase();

    const localMatch = (item) => {
        const haystacks = [
            item.product_calculator_name,
            item.category,
            item.root_section,
            item.section,
            item.manufacturer,
        ];
        return haystacks.some((value) => (value || "").toLowerCase().includes(searchNormalized));
    };

    // Умный поиск (smartScoreByName) знает только про номенклатуру, поэтому
    // он дополняет локальный фильтр, а не заменяет его — иначе совпадения по
    // категории/разделу/производителю пропадали бы, как только backend
    // успешно отвечает.
    const filtered = catalogData.filter((item) => {
        const matchesRegion = !region || item.region === region;
        if (!matchesRegion) return false;
        if (!searchNormalized) return true;
        if (smartScoreByName?.has(item.product_calculator_name || "")) return true;
        return localMatch(item);
    });

    const groups = new Map();
    filtered.forEach((item) => {
        const name = item.product_calculator_name || "Без названия";
        if (!groups.has(name)) groups.set(name, []);
        groups.get(name).push(item);
    });

    const priceField = priceBasis === "cash" ? "price_cash" : "price_non_cash";

    const result = Array.from(groups.
        entries()).map(([name, rows]) => {
            const sortedRows = [...rows].sort((a, b) => {
                const priceA = parsePrice(a[priceField]);
                const priceB = parsePrice(b[priceField]);
                if (priceA === null) return 1;
                if (priceB === null) return -1;
                return priceA - priceB;
            });
            const prices = sortedRows.map((row) => parsePrice(row[priceField])).filter((p) => p !== null);
            const minPrice = prices.length ? Math.min(...prices) : null;
            return { name, rows: sortedRows, minPrice };
        });

    if (smartScoreByName && searchNormalized) {
        return result.sort((a, b) => {
            const scoreA = smartScoreByName.get(a.name) ?? -1;
            const scoreB = smartScoreByName.get(b.name) ?? -1;
            if (scoreA !== scoreB) return scoreB - scoreA;
            return a.name.localeCompare(b.name, "ru");
        });
    }

    return result.sort((a, b) => a.name.localeCompare(b.name, "ru"));
};

export const groupItemsByField = (groupedItems, field, priceBasis) => {
    const priceField = priceBasis === "cash" ? "price_cash" : "price_non_cash";

    const groups = new Map();


    groupedItems.forEach((itemGroup) => {
        itemGroup.rows.forEach((row) => {
            const groupName = row[field] || "Не указано";

            if (!groups.has(groupName)) {
                groups.set(groupName, new Map());
            }

            const products = groups.get(groupName);

            if (!products.has(itemGroup.name)) {
                products.set(itemGroup.name, []);
            }

            products.get(itemGroup.name).push(row);
        });
    });

    return Array.from(groups.entries())
        .map(([name, products]) => ({
            name,

            items: Array.from(products.entries())
                .map(([productName, rows]) => {
                    const sortedRows = [...rows].sort((a, b) => {
                        const priceA = parsePrice(a[priceField]);
                        const priceB = parsePrice(b[priceField]);

                        if (priceA === null) return 1;
                        if (priceB === null) return -1;

                        return priceA - priceB;
                    });

                    const prices = sortedRows
                        .map((row) => parsePrice(row[priceField]))
                        .filter((price) => price !== null);

                    return {
                        name: productName,
                        rows: sortedRows,
                        minPrice: prices.length
                            ? Math.min(...prices)
                            : null,
                    };
                })
                .sort((a, b) =>
                    a.name.localeCompare(b.name, "ru")
                ),
        }))
        .sort((a, b) =>
            a.name.localeCompare(b.name, "ru")
        );
};

// Группировка в дерево по нескольким полям подряд (вкладка "По категориям").
//
// Поля берутся из строк каталога: root_section / section — это разделы Битрикса,
// которые бэкенд подставляет из category_section_mapping (см. эндпоинт
// /api/adminka/product-catalog). Дерево получается: корневой раздел -> раздел ->
// номенклатуры.
//
// Возвращает узлы вида { name, minPrice, children } для промежуточных уровней и
// { name, minPrice, items } для последнего — items совпадает по форме с выдачей
// groupItemsByField, поэтому список умеет рисовать оба варианта одинаково.
//
// Категория без сопоставления с разделом (в каталоге таких около 25 из 371)
// попадает в узел "Без раздела", а вторым уровнем у неё идёт своя же категория —
// чтобы такие товары не сваливались в одну кучу.
export const groupItemsByTree = (groupedItems, fields, priceBasis) => {
    const priceField = priceBasis === "cash" ? "price_cash" : "price_non_cash";

    const fallbackFor = (row, fieldIndex) =>
        fieldIndex === 0
            ? "Без раздела"
            : (row.category || "Прочее");

    const root = new Map();

    groupedItems.forEach((itemGroup) => {
        itemGroup.rows.forEach((row) => {
            const path = fields.map((field, index) => {
                const value = row[field];
                return (value === null || value === undefined || value === "")
                    ? fallbackFor(row, index)
                    : value;
            });

            let level = root;

            path.forEach((segment, index) => {
                if (!level.has(segment)) {
                    level.set(segment, index === path.length - 1
                        ? { products: new Map() }
                        : { children: new Map() });
                }

                const node = level.get(segment);

                if (index === path.length - 1) {
                    if (!node.products.has(itemGroup.name)) {
                        node.products.set(itemGroup.name, []);
                    }
                    node.products.get(itemGroup.name).push(row);
                } else {
                    level = node.children;
                }
            });
        });
    });

    const buildItems = (products) =>
        Array.from(products.entries())
            .map(([productName, rows]) => {
                const sortedRows = [...rows].sort((a, b) => {
                    const priceA = parsePrice(a[priceField]);
                    const priceB = parsePrice(b[priceField]);

                    if (priceA === null) return 1;
                    if (priceB === null) return -1;

                    return priceA - priceB;
                });

                const prices = sortedRows
                    .map((row) => parsePrice(row[priceField]))
                    .filter((price) => price !== null);

                return {
                    name: productName,
                    rows: sortedRows,
                    minPrice: prices.length ? Math.min(...prices) : null,
                };
            })
            .sort((a, b) => a.name.localeCompare(b.name, "ru"));

    const minOf = (values) => {
        const real = values.filter((v) => v !== null && v !== undefined && !Number.isNaN(v));
        return real.length ? Math.min(...real) : null;
    };

    const buildLevel = (level) =>
        Array.from(level.entries())
            .map(([name, node]) => {
                if (node.products) {
                    const items = buildItems(node.products);
                    return {
                        name,
                        items,
                        minPrice: minOf(items.map((item) => item.minPrice)),
                    };
                }

                const children = buildLevel(node.children);
                return {
                    name,
                    children,
                    minPrice: minOf(children.map((child) => child.minPrice)),
                };
            })
            .sort((a, b) => a.name.localeCompare(b.name, "ru"));

    return buildLevel(root);
};
